import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { Rules } from 'modules/journey/components/RuleBuilder';
import { saveExitRulesMutation } from 'modules/journey/components/ExitRules/api/exitRulesMutations';
import { transformExitRulesForMutation } from 'modules/journey/components/ExitRules/ExitRules.utils';
import { getWorkflow } from '../ducks';
import { useExitRuleContext } from '../context/ExitRuleContext';

const SUCCESS_MESSAGE = 'Success! Updated exit rules';
const FAILURE_MESSAGE = `Error updating exit rules. Please try again later.`;

type useUpdateExitRulesProps = {
  exitRules: Rules;
  onSuccess?: () => void;
  showSuccessToast?: boolean;
};

export const useUpdateExitRules = () => {
  const dispatch = useDispatch();
  const { getExitRuleCriteria } = useExitRuleContext();
  const workflow = useSelector(getWorkflow);
  const { id: workflowId, workflowType } = workflow;

  const [saveExitRules] = useMutation(saveExitRulesMutation);

  return async ({
    exitRules,
    onSuccess,
    showSuccessToast = false,
  }: useUpdateExitRulesProps): Promise<any> => {
    try {
      const transformedRules = transformExitRulesForMutation(exitRules);
      const { data } = await saveExitRules({
        variables: {
          workflowId,
          workflowType,
          rules: transformedRules,
        },
      });
      if (data?.saveExitRules?.rules) {
        getExitRuleCriteria();
        if (onSuccess) onSuccess();
        if (showSuccessToast) dispatch(addPositiveToast(SUCCESS_MESSAGE));
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useUpdateExitRules'));
      }
      return { hasErrors: !data };
    } catch (ex) {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useUpdateExitRules'));
      return { hasErrors: true };
    }
  };
};
